// components/AI/noteNav.js
// Helper navigasi rujukan sumber AI.
//
// ⚠ ASUMSI LAMA YANG SALAH: setiap dokumen KB dianggap berasal dari Notes,
// sehingga klik sumber selalu menavigasi ke /dashboard/notes?note=<doc_id>.
// Itu benar hanya untuk source 'notes'. Dokumen hasil generate — mis.
// 'sap_customizing' yang ditulis oleh aiKbSap.js — hidup di kb_docs dan TIDAK
// punya baris apa pun di tabel notes. Halaman Notes tidak menemukan id itu,
// lalu jatuh ke note pertama pada daftar. Yang terlihat oleh pengguna: sumber
// yang sama sekali tidak nyambung — seolah retrieval-nya salah, padahal
// retrievalnya benar dan hanya tautannya yang keliru.
//
// Sekarang berjalan dua jalur:
//   - SEMUA sumber  → buka modal isi chunk (verifikasi: teks yang benar-benar
//     dibaca model, bukan halaman turunannya).
//   - source 'notes' → di dalam modal tersedia tombol untuk membuka note asli,
//     yang memakai jalur lama di bawah ini.

// Jalur lama: navigasi ke halaman Notes. Dipakai HANYA untuk source 'notes'.
export function gotoNote(s) {
  if (!s || s.doc_id == null) return;
  const detail = {
    noteId: s.doc_id,
    tab: Number.isInteger(s.tab) ? s.tab : 0,
    snippet: String(s.snippet || ''),
  };
  if (window.location.pathname.startsWith('/dashboard/notes')) {
    window.dispatchEvent(new CustomEvent('ai:open-note', { detail }));
  } else {
    window.dispatchEvent(new CustomEvent('ai:goto-note', { detail }));
  }
}

// Apakah sumber ini benar-benar sebuah note?
// kb_source kosong = respons lama (sebelum field ini ada). Diperlakukan sebagai
// note agar chat yang tersimpan sebelum perubahan ini tetap berperilaku sama.
export function isNoteSource(s) {
  const k = String((s && s.kb_source) || '').trim();
  return k === '' || k === 'notes';
}

// Titik masuk tunggal untuk klik sumber — dari chip maupun sitasi [n].
// Modal dirender di AIChat; event dipakai supaya kedua pemanggil tidak perlu
// tahu di mana modal itu hidup.
export function openNoteRef(s) {
  if (!s || s.doc_id == null) return;
  window.dispatchEvent(new CustomEvent('ai:open-kb-doc', { detail: s }));
}

// ── Sumber KB → halaman dashboard asalnya ───────────────────────────────────
// Modal memperlihatkan teks yang dibaca model; ini melanjutkannya ke DATA
// aslinya, supaya verifikasi tidak berhenti pada narasi yang dihasilkan.
//
// ⚠ NAMA OBJEK DIAMBIL DARI JUDUL, BUKAN doc_id. doc_id memakai `_` sebagai
// pemisah sekaligus sebagai bagian nama objek — `enh_14_ZMM00005_MM06E005_
// EXIT_SAPMM06E_016` tidak punya batas pasti antara project, enhancement, dan
// komponen, jadi setiap pemisahan hanyalah tebakan. Judul jauh lebih tertib:
//   "SAP D01 — EXIT_SAPMM06E_016 (…) · modul MM · project ZMM00005 · …"
//
// Taruhannya kecil DENGAN SENGAJA: hasilnya cuma mengisi kotak PENCARIAN di
// halaman tujuan. Token yang meleset menampilkan beberapa baris berlebih —
// bukan layar kosong. Kalau ini dipakai untuk memilih satu baris secara
// langsung, tebakan yang salah akan berakhir sebagai "objek tidak ditemukan"
// dan terlihat seperti datanya hilang.
function objectNameFromTitle(title) {
  const s = String(title || '');
  const dash = s.indexOf('—');
  if (dash < 0) return '';
  // Berhenti pada pembatas judul yang mana pun yang lebih dulu muncul.
  const rest = s.slice(dash + 1).trim();
  const cut = [' (', ' · '].reduce((m, sep) => {
    const i = rest.indexOf(sep);
    return i >= 0 && (m < 0 || i < m) ? i : m;
  }, -1);
  return (cut >= 0 ? rest.slice(0, cut) : rest).trim();
}

// Pecah doc_id hanya sejauh yang AMAN: prefiks dan connection_id saja. Sisanya
// tidak disentuh, karena di situlah ambiguitas `_` berada.
function headOfDocId(docId) {
  const m = /^([a-z_]+?)_(\d+)_(.*)$/.exec(String(docId || ''));
  if (!m) return null;
  return { kind: m[1], connId: m[2], tail: m[3] };
}

/**
 * Tujuan navigasi untuk sebuah sumber, atau null bila tidak ada yang masuk akal.
 * @returns {{ path: string, params: Object, label: string }|null}
 */
export function dashboardTargetOf(s) {
  if (!s) return null;
  const src = String(s.kb_source || '').trim();
  if (src !== 'sap_customizing') return null;

  const docId = String(s.doc_id || '');
  // Dokumen status transport bersifat LANDSCAPE — ia tidak membicarakan satu
  // objek, jadi tidak ada baris yang bisa dituju. Menautkannya ke halaman tanpa
  // filter hanya memindahkan pengguna tanpa membawanya ke mana-mana.
  if (docId.indexOf('sap_transport_status') === 0) return null;

  const h = headOfDocId(docId);
  if (!h) return null;

  const path = '/dashboard/sap-customizing';
  const base = { conn: h.connId };

  if (h.kind === 'proj') return { path, params: { ...base, view: 'enh', project: h.tail }, label: h.tail };
  if (h.kind === 'mod')  return { path, params: { ...base, view: 'enh', module: h.tail },  label: h.tail };

  const isBadi = h.kind === 'badi';
  const view = isBadi ? 'badi' : 'enh';
  if (h.tail === 'OVERVIEW') return { path, params: { ...base, view }, label: '' };

  const name = objectNameFromTitle(s.title);
  if (!name) return { path, params: { ...base, view }, label: '' };
  return { path, params: { ...base, view, q: name }, label: name };
}

// Navigasi dijalankan lewat event karena AIChat hidup di luar Router;
// DashboardContainer yang di dalam Router menangkapnya dan memanggil navigate().
// Pola yang sama persis dipakai 'ai:goto-note'.
export function gotoDashboard(target) {
  if (!target || !target.path) return;
  window.dispatchEvent(new CustomEvent('ai:goto-page', { detail: target }));
}
